import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import '../../styles/ReviewExam.css';
import '../../styles/Stats.css';
import BeatLoader from 'react-spinners/BeatLoader';
import mixpanel from '../../utils/mixpanel'
import { API_URL } from '../../app/constants'

// const MOCK_EXAM_RESPONSES_API_URL = `${API_URL}/api/v1/mock-exam/user/responses`;

function MockExamReview() {
  const navigate = useNavigate();
  const [examData, setExamData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showWrongOnly, setShowWrongOnly] = useState(false);

  useEffect(() => {
    // Add fullscreen class to body when component mounts
    document.body.classList.add('review-personalized-fullscreen');

    const fetchExamReview = async () => {
      try {
        setLoading(true);
        const jwt = localStorage.getItem('jwt');
        const apiUrl = `${API_URL}/api/v1/mock-exam/user/responses`;

        const response = await fetch(apiUrl, {
          method: 'GET',
          headers: { 'Authorization': `Bearer ${jwt}`, 'Content-Type': 'application/json' },
        });
        const data = await response.json();
        console.log('API Response:', data);
        
        // Process the data from the mock exam responses
        let processedData;
        if (data.responses) {
          processedData = data.responses.map(response => {
            return {
              question_id: response.question_id,
              question_text: response.question_text,
              selected_answer: response.selected_answer,
              is_correct: response.is_correct,
              correct_answer: response.correct_answer,
              choices: response.choices,
              explanation: response.explanation,
              instruction: response.instruction,
              script: response.script,
              time_taken: response.time_taken || 0
            };
          });
        } else {
          processedData = data;
        }

        // Store data for both old and new API formats
        setExamData({
          responses: processedData,
          completion_time: data.completion_time || 0
        });
        mixpanel.track('Review Mock Exam', {
          score: calculateScore(data)
        });
      } catch (error) {
        console.error('Error fetching review:', error);
        setError('Failed to fetch mock exam review.');
      }
      setLoading(false);
    };

    fetchExamReview();

    // Clean up function to remove the class when component unmounts
    return () => {
      document.body.classList.remove('review-personalized-fullscreen');
    };
  }, []);

  function calculateScore(data) {
    // Handle both old and new data formats
    const responses = data.responses || data;

    if (!responses || responses.length === 0) return '0/0';
    // If we have is_correct field, use it
    if (responses[0] && 'is_correct' in responses[0]) {
      const correctAnswers = responses.filter(r => r.is_correct).length;
      return `${correctAnswers}/${responses.length}`;
    } else if (responses[0] && (responses[0].user_answer || responses[0].selected_answer)) {
      // For the user endpoint that includes user answers
      const correctAnswers = responses.filter(r =>
        (r.user_answer === r.correct_answer) ||
        (r.selected_answer === r.correct_answer)
      ).length;
      return `${correctAnswers}/${responses.length}`;
    } else {
      // Fallback if we don't have user answers
      return `0/${responses.length}`;
    }
  }

  const calculateTotalTime = (data) => {
    console.log("Calculating total time with data:", data);

    // First check if the API response includes the completion_time field
    if (data && data.completion_time) {
      console.log("Found completion_time in data:", data.completion_time);
      return data.completion_time;
    }

    // If there's no completion_time field but there are responses with time_taken
    if (Array.isArray(data) && data.length > 0) {
      const total = data.reduce((total, response) => total + (response.time_taken || 0), 0);
      console.log("Calculated time from time_taken fields:", total);
      return total;
    }

    // Default to 8 for now
    console.log("No time data found, using default of 8 seconds");
    return 8;
  };

  const handleQuestionClick = (questionId, questionIndex) => {
    // Handle the new data structure
    const responses = examData.responses || examData;
    
    // Find the clicked question
    const clickedQuestion = responses.find(q => q.question_id === questionId);
    if (!clickedQuestion) return;

    console.log("Clicked question:", clickedQuestion);

    // Make sure all necessary properties are included in the data passed to the review component
    const completeExamData = responses.map(response => {
      return {
        ...response
      };
    });

    // Find the index of the clicked question in finalExamData
    const targetIndex = completeExamData.findIndex(q => q.question_id === questionId);
    
    console.log(`Navigating to question at index ${targetIndex >= 0 ? targetIndex : 0} (question ID: ${questionId})`);
    
    navigate(`/review-question/mock-exam`, { state: { examData: completeExamData, currentContentIndex: targetIndex >= 0 ? targetIndex : 0 } });
  };

  const handleBackToDashboard = () => {
    navigate('/');
  };

  if (loading) return <div className="loader-container"><BeatLoader color="#3E00EF" /></div>;
  if (error) return <div>{error}</div>;
  
  // Handle new data structure
  const responses = examData && (examData.responses || examData);
  if (!responses || responses.length === 0) return <div>No exam data available.</div>;

  // Format questions for the ResultsReview component
  const formattedQuestions = responses.map((response) => {
    console.log("Response data:", response); // Log to see the response structure

    return {
      id: response.question_id,
      text: response.question_text || response.question,
      isCorrect: response.is_correct || false,
      isBookmarked: false, // We can implement bookmarking later
      correctAnswer: response.correct_answer || response.explanation,
      answers: [{
        isCorrect: response.is_correct || false,
        correctText: response.correct_answer
      }]
    };
  });

  // Calculate total time spent
  const totalTime = calculateTotalTime(examData);

  // Format time from seconds to mm:ss
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  };

  // Calculate score from formattedQuestions
  const totalQuestions = formattedQuestions.length;
  const correctAnswers = formattedQuestions.filter(q => q.isCorrect).length;
  const scorePercentage = totalQuestions > 0 ? Math.round((correctAnswers / totalQuestions) * 100) : 0;

  // New study results page design based on study_result.png
  return (
    <div className="analytics review-personalized-fullscreen" style={{
      padding: '20px 16px',
      fontFamily: "'Manrope', sans-serif",
      background: 'white',
      overflowY: 'auto'
    }}>
      {/* Header with title and buttons */}
      <div className="analytics-header" style={{
        maxWidth: '1000px',
        margin: '0 auto 24px auto',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-end'
      }}>
        <h1 style={{ margin: '0', alignSelf: 'flex-end', fontSize: '20px' }}>Study Results</h1>

        <button
          onClick={handleBackToDashboard}
          style={{
            padding: '10px 16px',
            borderRadius: '8px',
            border: 'none',
            background: '#9333EA',
            color: 'white',
            fontWeight: '600',
            fontSize: '14px',
            cursor: 'pointer',
            whiteSpace: 'nowrap',
            alignSelf: 'flex-end'
          }}
        >
          <span className="desktop-only" style={{ display: 'none' }}>Let's Train!</span>
          <span className="mobile-only">Let's Train!</span>
        </button>
      </div>

      {/* Performance Summary Card */}
      <div className="analytics-section" style={{
        maxWidth: '1000px',
        margin: '0 auto 24px auto'
      }}>
        <div className="section-title">
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#9333EA" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="section-icon">
            <path d="M12 20V10"/>
            <path d="M18 20V4"/>
            <path d="M6 20v-4"/>
          </svg>
          <h2>Performance Summary</h2>
        </div>

        <div className="summary-grid">
          {/* Score Card */}
          <div className="summary-stat" style={{textAlign: 'center', padding: '20px'}}>
            <div className="stat-header" style={{textAlign: 'center'}}>Score</div>

            <div style={{display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: '8px'}}>
              <div className="stat-value" style={{fontSize: '32px', marginBottom: '8px'}}>{scorePercentage}%</div>
              <div style={{color: '#6B7280', fontSize: '16px'}}>{correctAnswers}/{totalQuestions}</div>
            </div>
          </div>

          {/* Time Card */}
          <div className="summary-stat" style={{textAlign: 'center', padding: '20px'}}>
            <div className="stat-header" style={{textAlign: 'center'}}>Time Spent</div>

            <div style={{display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: '8px'}}>
              <div className="stat-value" style={{fontSize: '32px', marginBottom: '8px'}}>{formatTime(totalTime)}</div>
            </div>

            <div style={{color: '#6B7280', fontSize: '14px', textAlign: 'center'}}>
              {examData.timer ? `Recommended time: ${formatTime(examData.timer)}` : 'Recommended time: not defined'}
            </div>
          </div>
        </div>
      </div>

      {/* Questions Results Section */}
      <div className="analytics-section" style={{
        maxWidth: '1000px',
        margin: '0 auto'
      }}>
        <div className="section-title">
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#9333EA" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="section-icon">
            <circle cx="12" cy="12" r="10"></circle>
            <path d="m9 12 2 2 4-4"></path>
          </svg>
          <h2>Answered Questions Results</h2>
        </div>

        {/* Filter checkbox */}
        <div style={{
          marginBottom: '16px',
          display: 'flex',
          alignItems: 'center',
          backgroundColor: '#F5F3FF',
          padding: '10px 12px',
          borderRadius: '8px'
        }}>
          <input
            type="checkbox"
            id="wrongOnly"
            style={{
              marginRight: '8px',
              width: '16px',
              height: '16px',
              accentColor: '#9333EA'
            }}
            checked={showWrongOnly}
            onChange={(e) => setShowWrongOnly(e.target.checked)}
          />
          <label htmlFor="wrongOnly" style={{ fontSize: '14px', color: '#4B5563', fontWeight: '500' }}>
            Show wrong answers only
          </label>
        </div>

        {/* Questions Grid */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
          gap: '16px'
        }}>
          {formattedQuestions
            .filter(question => !showWrongOnly || !question.isCorrect)
            .map((question, i) => {
              // Find the original question number (1-based index in the full list)
              const originalQuestionNumber = formattedQuestions.findIndex(q => q.id === question.id) + 1;
              
              return (
                <div
                  key={question.id}
                  style={{
                    border: '1px solid #E5E7EB',
                    borderRadius: '8px',
                    padding: '14px',
                    cursor: 'pointer',
                    transition: 'transform 0.2s, box-shadow 0.2s',
                    position: 'relative',
                    height: '170px',
                    display: 'flex',
                    flexDirection: 'column'
                  }}
                  onClick={() => {
                    // Get the filtered question index and pass it along with the question ID
                    const filteredIndex = formattedQuestions
                      .filter(q => !showWrongOnly || !q.isCorrect)
                      .findIndex(q => q.id === question.id);
                      
                    // Find the actual index in the responses array
                    const actualIndex = responses.findIndex(r => r.question_id === question.id);
                    
                    console.log(`Clicked question ${originalQuestionNumber} (filtered index: ${filteredIndex}, actual index: ${actualIndex})`);
                    handleQuestionClick(question.id, actualIndex);
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.1)';
                    e.currentTarget.style.borderColor = '#9333EA';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'none';
                    e.currentTarget.style.boxShadow = 'none';
                    e.currentTarget.style.borderColor = '#E5E7EB';
                  }}
                >
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    marginBottom: '8px'
                  }}>
                    <div style={{ fontSize: '16px', fontWeight: '600', color: '#111827' }}>
                      Question {originalQuestionNumber}
                    </div>

                    <div>
                      {question.isCorrect ? (
                        <div style={{ color: '#10B981', fontSize: '24px' }}>✓</div>
                      ) : (
                        <div style={{ color: '#EF4444', fontSize: '24px' }}>✕</div>
                      )}
                    </div>
                  </div>

                  <div style={{
                    fontSize: '14px',
                    color: '#111827',
                    marginBottom: '16px',
                    flexGrow: 1,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: '-webkit-box',
                    WebkitLineClamp: 3,
                    WebkitBoxOrient: 'vertical'
                  }}>
                    {question.text}
                  </div>

                  {!question.isCorrect && (
                    <div style={{
                      fontSize: '14px',
                      color: '#10B981',
                      display: 'flex',
                      marginTop: 'auto'
                    }}>
                      <span style={{ fontWeight: '600', marginRight: '4px' }}>Correct:</span>
                      <span>{question.correctAnswer}</span>
                    </div>
                  )}
                </div>
              );
            })}
        </div>
      </div>
    </div>
  );
}

export default MockExamReview;